import type {
  ContractChargeKind,
  ContractInputBody,
  ContractInvoiceMode,
  ContractPeriodicity,
  ContractStatus,
  ContractView,
  NfseServiceInput,
  PeriodPrint,
  ServiceInvoiceEventView,
  ServiceInvoiceInputBody,
  ServiceInvoiceStatus,
  ServiceInvoiceView,
} from "@cx/contracts";
import {
  MAX_CONTRACT_COMPETENCE_AHEAD,
  MAX_CONTRACT_COMPETENCE_BACK,
  addCompetence,
  formatCompetence,
} from "@cx/domain";
import type { Tone } from "@cx/ui";

/** Contratos, cobranças e NFS-e (T2.8): textos e regras das telas, sem React (testados em labels.test.ts). */

export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = {
  active: "Ativo",
  inactive: "Inativo",
  settled: "Baixado",
  exempt: "Isento",
  evaluation: "Em avaliação",
};
export const CONTRACT_STATUS_TONE: Record<ContractStatus, Tone> = {
  active: "success",
  inactive: "secondary",
  settled: "secondary",
  exempt: "info",
  evaluation: "warning",
};

export const PERIODICITY_LABEL: Record<ContractPeriodicity, string> = {
  monthly: "Mensal",
  bimonthly: "Bimestral",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
  biennial: "Bienal",
  triennial: "Trienal",
};

export const PERIOD_PRINT_LABEL: Record<PeriodPrint, string> = {
  none: "Não imprimir",
  current_month: "Mês da competência",
  previous_month: "Mês anterior à competência",
};

export const DUE_OFFSET_LABEL: Record<0 | 1 | 2, string> = {
  0: "No mês da competência",
  1: "No mês seguinte",
  2: "Dois meses depois",
};

export const INVOICE_MODE_LABEL: Record<ContractInvoiceMode, string> = {
  none: "Não emitir",
  on_charge: "Emitir NFS-e com a cobrança",
};

export const CHARGE_KIND_LABEL: Record<ContractChargeKind, string> = {
  none: "Sem cobrança automática",
  pix: "Pix",
};

export const NFSE_STATUS_LABEL: Record<ServiceInvoiceStatus, string> = {
  draft: "Rascunho",
  queued: "Na fila de envio",
  sending: "Enviando à prefeitura",
  authorized: "Autorizada",
  rejected: "Rejeitada",
  canceled: "Cancelada",
};
export const NFSE_STATUS_TONE: Record<ServiceInvoiceStatus, Tone> = {
  draft: "secondary",
  queued: "info",
  sending: "info",
  authorized: "success",
  rejected: "danger",
  canceled: "secondary",
};

export const NFSE_EVENT_STATUS_LABEL: Record<ServiceInvoiceEventView["status"], string> = {
  pending: "Na fila de envio",
  sending: "Enviando à prefeitura",
  registered: "Registrado",
  rejected: "Recusado",
};

export const NATURE_LABEL: Record<1 | 2 | 3 | 4, string> = {
  1: "Operação tributável",
  2: "Imunidade",
  3: "Exportação de serviço",
  4: "Não incidência",
};

export const SPECIAL_REGIME_LABEL: Record<0 | 1 | 2 | 3 | 4 | 5 | 6, string> = {
  0: "Nenhum",
  1: "Ato cooperado",
  2: "Estimativa",
  3: "Microempresa municipal",
  4: "Notário ou registrador",
  5: "Profissional autônomo",
  6: "Sociedade de profissionais",
};

/** NFS-e que ainda muda sozinha (a tela consulta de novo). */
export const nfseInFlight = (s: ServiceInvoiceStatus) => s === "queued" || s === "sending";
export const nfseEventInFlight = (e: Pick<ServiceInvoiceEventView, "status">) =>
  e.status === "pending" || e.status === "sending";

/** "NFS-e nº 123", "NFS-e (rascunho)". */
export const nfseTitle = (d: { nfseNumber: string | null; status: ServiceInvoiceStatus }) =>
  d.nfseNumber ? `NFS-e nº ${d.nfseNumber}` : d.status === "draft" ? "NFS-e (rascunho)" : "NFS-e";

/** Mensagem da prefeitura/provedor: "E0312 — …". */
export const nfseMessage = (code: string | null, reason: string | null) =>
  reason ? (code && code !== "provider" ? `${code} — ${reason}` : reason) : null;

/** Competência atual (AAAA-MM) em São Paulo. */
export const competenceOfDate = (today: string) => today.slice(0, 7);

/** Competências aceitas para "Gerar cobranças": do mês seguinte até 12 meses atrás (mais recente primeiro). */
export function competenceOptions(today: string): { value: string; label: string }[] {
  const now = competenceOfDate(today);
  const out: { value: string; label: string }[] = [];
  for (let k = MAX_CONTRACT_COMPETENCE_AHEAD; k >= -MAX_CONTRACT_COMPETENCE_BACK; k--) {
    const c = addCompetence(now, k);
    out.push({ value: c, label: formatCompetence(c) });
  }
  return out;
}

// ---------- formulário do serviço ----------

export type ServiceForm = {
  cTribNac: string;
  cTribMun: string;
  cnae: string;
  nbs: string;
  description: string;
};
export const emptyServiceForm = (): ServiceForm => ({
  cTribNac: "",
  cTribMun: "",
  cnae: "",
  nbs: "",
  description: "",
});
export const serviceFormOf = (s: NfseServiceInput | null): ServiceForm =>
  s
    ? {
        cTribNac: s.cTribNac,
        cTribMun: s.cTribMun ?? "",
        cnae: s.cnae ?? "",
        nbs: s.nbs ?? "",
        description: s.description,
      }
    : emptyServiceForm();
export const serviceBody = (f: ServiceForm) => ({
  cTribNac: f.cTribNac,
  cTribMun: f.cTribMun.trim() || null,
  cnae: f.cnae.trim() || null,
  nbs: f.nbs.trim() || null,
  description: f.description,
});

// ---------- contrato ----------

type Ref = { id: string; name: string } | null;

export type ContractForm = {
  contact: Ref;
  billTo: Ref;
  number: string;
  description: string;
  status: ContractStatus;
  contractDate: string;
  amountCents: number | null;
  baseDate: string;
  dueMonthOffset: 0 | 1 | 2;
  dueDay: string;
  periodicity: ContractPeriodicity;
  expectedTotalCents: number | null;
  periodPrint: PeriodPrint;
  invoiceMode: ContractInvoiceMode;
  chargeKind: ContractChargeKind;
  /** Serviço próprio do contrato (senão o padrão da configuração da NFS-e). */
  ownService: boolean;
  service: ServiceForm;
  paymentMethodId: string;
  financialAccountId: string;
  financialCategoryId: string;
  sellerId: string;
  notes: string;
};

export const emptyContractForm = (today: string): ContractForm => ({
  contact: null,
  billTo: null,
  number: "",
  description: "",
  status: "active",
  contractDate: today,
  amountCents: null,
  baseDate: "",
  dueMonthOffset: 0,
  dueDay: "10",
  periodicity: "monthly",
  expectedTotalCents: null,
  periodPrint: "none",
  invoiceMode: "none",
  chargeKind: "none",
  ownService: false,
  service: emptyServiceForm(),
  paymentMethodId: "",
  financialAccountId: "",
  financialCategoryId: "",
  sellerId: "",
  notes: "",
});

export const contractFormOf = (c: ContractView): ContractForm => ({
  contact: c.contact,
  billTo: c.billTo,
  number: String(c.number),
  description: c.description ?? "",
  status: c.status,
  contractDate: c.contractDate,
  amountCents: c.amountCents,
  baseDate: c.baseDate ?? "",
  dueMonthOffset: c.dueMonthOffset as 0 | 1 | 2,
  dueDay: String(c.dueDay),
  periodicity: c.periodicity,
  expectedTotalCents: c.expectedTotalCents,
  periodPrint: c.periodPrint,
  invoiceMode: c.invoiceMode,
  chargeKind: c.chargeKind,
  ownService: c.service !== null,
  service: serviceFormOf(c.service),
  paymentMethodId: c.paymentMethod?.id ?? "",
  financialAccountId: c.financialAccount?.id ?? "",
  financialCategoryId: c.financialCategory?.id ?? "",
  sellerId: c.seller?.id ?? "",
  notes: c.notes ?? "",
});

/** Corpo do POST/PUT /contracts. Campos vazios vão como null; a API confere tudo de novo. */
export function contractBody(f: ContractForm): ContractInputBody {
  const n = f.number.trim();
  return {
    contactId: f.contact?.id ?? "",
    billToContactId: f.billTo?.id ?? null,
    number: n ? Number(n) : null,
    description: f.description.trim() || null,
    status: f.status,
    contractDate: f.contractDate,
    amountCents: f.amountCents ?? 0,
    baseDate: f.baseDate || null,
    dueMonthOffset: f.dueMonthOffset,
    dueDay: Number(f.dueDay),
    periodicity: f.periodicity,
    expectedTotalCents: f.expectedTotalCents,
    periodPrint: f.periodPrint,
    invoiceMode: f.invoiceMode,
    chargeKind: f.chargeKind,
    service: f.ownService ? serviceBody(f.service) : null,
    paymentMethodId: f.paymentMethodId || null,
    financialAccountId: f.financialAccountId || null,
    financialCategoryId: f.financialCategoryId || null,
    sellerId: f.sellerId || null,
    notes: f.notes.trim() || null,
  };
}

/** Vencimento em palavras: "dia 10 do mês seguinte". */
export const dueText = (c: { dueDay: number; dueMonthOffset: number }) =>
  `Dia ${c.dueDay} ${c.dueMonthOffset === 0 ? "do mês da competência" : c.dueMonthOffset === 1 ? "do mês seguinte" : "de dois meses depois"}`;

// ---------- NFS-e avulsa ----------

export type NfseForm = {
  contact: Ref;
  service: ServiceForm;
  nature: 1 | 2 | 3 | 4;
  immunityType: string;
  exportCountry: string;
  servicesCents: number | null;
  discountCents: number | null;
  deductionsCents: number | null;
  issPct4: number | null;
  issWithheld: boolean;
  pisPct4: number | null;
  cofinsPct4: number | null;
  csllPct4: number | null;
  irPct4: number | null;
  inssPct4: number | null;
  complementaryInfo: string;
};

export const emptyNfseForm = (): NfseForm => ({
  contact: null,
  service: emptyServiceForm(),
  nature: 1,
  immunityType: "",
  exportCountry: "",
  servicesCents: null,
  discountCents: null,
  deductionsCents: null,
  issPct4: null,
  issWithheld: false,
  pisPct4: null,
  cofinsPct4: null,
  csllPct4: null,
  irPct4: null,
  inssPct4: null,
  complementaryInfo: "",
});

export const nfseFormOf = (d: ServiceInvoiceView): NfseForm => ({
  contact: d.contactId ? { id: d.contactId, name: d.recipient.name } : null,
  service: serviceFormOf(d.service),
  nature: d.nature,
  immunityType: d.immunityType === null ? "" : String(d.immunityType),
  exportCountry: d.exportCountry ?? "",
  servicesCents: d.servicesCents,
  discountCents: d.discountCents,
  deductionsCents: d.deductionsCents,
  issPct4: d.issPct4,
  issWithheld: d.issWithheld,
  pisPct4: d.retentions.pisPct4,
  cofinsPct4: d.retentions.cofinsPct4,
  csllPct4: d.retentions.csllPct4,
  irPct4: d.retentions.irPct4,
  inssPct4: d.retentions.inssPct4,
  complementaryInfo: d.complementaryInfo ?? "",
});

export function nfseBody(f: NfseForm): ServiceInvoiceInputBody {
  return {
    contactId: f.contact?.id ?? "",
    service: serviceBody(f.service),
    nature: f.nature,
    immunityType:
      f.nature === 2 && f.immunityType !== ""
        ? (Number(f.immunityType) as ServiceInvoiceInputBody["immunityType"])
        : null,
    exportCountry: f.nature === 3 ? f.exportCountry.trim() || null : null,
    servicesCents: f.servicesCents ?? 0,
    discountCents: f.discountCents ?? 0,
    deductionsCents: f.deductionsCents ?? 0,
    issPct4: f.issPct4,
    issWithheld: f.issWithheld,
    retentions: {
      pisPct4: f.pisPct4 ?? 0,
      cofinsPct4: f.cofinsPct4 ?? 0,
      csllPct4: f.csllPct4 ?? 0,
      irPct4: f.irPct4 ?? 0,
      inssPct4: f.inssPct4 ?? 0,
    },
    complementaryInfo: f.complementaryInfo.trim() || null,
  };
}

/** Data (dd/mm/aaaa) de um instante ISO, no fuso de São Paulo. */
export const isoDateBR = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });

/** Data e hora (dd/mm/aaaa hh:mm) de um instante ISO, no fuso de São Paulo. */
export const isoDateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });
